import { z } from 'zod';

import { envVarsSchema } from './types';

/**
 * Env vars must be strings, so we convert them to booleans in code.
 */
const stringToBool = z
  .enum(['true', 'false'])
  .default('false')
  .transform((value) => value === 'true');

/**
 * Secrets must never be shared with the browser, or committed to code.
 * Can be set per-environment by updating the matching secret in AWS Secrets Manager.
 * Can be overridden locally by using a .env.local file (please ensure gitignore).
 */
const secretsSchema = z.object({
  CONTENTFUL_SPACE_ID: z.string(),
  CONTENTFUL_ACCESS_TOKEN: z.string(),
  CONTENTFUL_PERSONAL_ACCESS_TOKEN: z.string(),
  CONTENTFUL_PREVIEW_ACCESS_TOKEN: z.string(),
  CONTENTFUL_REVALIDATE_SECRET: z.string(),
  CONTENTFUL_PREVIEW_SECRET: z.string(),
  CONTENTFUL_ENVIRONMENT_ID: z.string(),
  CONTENTFUL_EDITOR_APP_ID: z.string(),
  CONNECT_PROXY_URL: z.string(),
  CONNECT_PROXY_KEY: z.string(),
  REDIRECT_API_URL: z.string(),
  REDIRECT_API_KEY: z.string(),
  JWT_ISSUER_LIST: z
    .string()
    .default('[]')
    .describe('A JSON array of valid JWT issuers.'),
});
export const getSecrets = () => {
  return secretsSchema.parse({
    CONTENTFUL_SPACE_ID: process.env.CONTENTFUL_SPACE_ID,
    CONTENTFUL_ACCESS_TOKEN: process.env.CONTENTFUL_ACCESS_TOKEN,
    CONTENTFUL_PERSONAL_ACCESS_TOKEN:
      process.env.CONTENTFUL_PERSONAL_ACCESS_TOKEN,
    CONTENTFUL_PREVIEW_ACCESS_TOKEN:
      process.env.CONTENTFUL_PREVIEW_ACCESS_TOKEN,
    CONTENTFUL_REVALIDATE_SECRET: process.env.CONTENTFUL_REVALIDATE_SECRET,
    CONTENTFUL_PREVIEW_SECRET: process.env.CONTENTFUL_PREVIEW_SECRET,
    CONTENTFUL_ENVIRONMENT_ID: process.env.CONTENTFUL_ENVIRONMENT_ID,
    CONTENTFUL_EDITOR_APP_ID: process.env.CONTENTFUL_EDITOR_APP_ID,
    CONNECT_PROXY_URL: process.env.CONNECT_PROXY_URL,
    CONNECT_PROXY_KEY: process.env.CONNECT_PROXY_KEY,
    REDIRECT_API_URL: process.env.REDIRECT_API_URL,
    REDIRECT_API_KEY: process.env.REDIRECT_API_KEY,
    JWT_ISSUER_LIST: process.env.JWT_ISSUER_LIST,
  });
};

/**
 * Public configuration that can be shared with the browser.
 * Can be set per-environment by updating the matching secret in AWS Secrets Manager.
 * Can be overridden locally by using a .env.local file.
 */
export const Sites = z.enum(['skippy', 'compex', 'my']);
const publicConfigSchema = z.object({
  NEXT_PUBLIC_SITE_ID: Sites.default('skippy').describe(
    'For multisite apps, which site to use.',
  ),
  NEXT_PUBLIC_BASE_URL_AUTH: z
    .string()
    .url()
    .describe('The url of the Auth app.'),
  NEXT_PUBLIC_BASE_URL_CART: z
    .string()
    .url()
    .describe('The url of the Cart app.'),
  NEXT_PUBLIC_BASE_URL_CHECKOUT: z
    .string()
    .url()
    .describe('The url of the Checkout app.'),
  NEXT_PUBLIC_BASE_URL_DOTCOM: z
    .string()
    .url()
    .describe(
      'The url of the Compassion website (allowing us to cutover from legacy to skippy at the right time).',
    ),
  NEXT_PUBLIC_BASE_URL_PROXY_API: z
    .string()
    .url()
    .describe('The url of the proxy API.'),
  NEXT_PUBLIC_BASE_URL_STORYBOOK: z
    .string()
    .url()
    .default('https://ark.devint.compassion.com/')
    .describe('The url of Storybook for the docs.'),
  NEXT_PUBLIC_GTM_ID: z
    .string()
    .optional()
    .describe('The id for Google Tag Manager.'),
  NEXT_PUBLIC_SKIPPY_SEARCH_API_URL: z
    .string()
    .url()
    .optional()
    .describe('The url of the Skippy Search API.'),
  NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY: z
    .string()
    .optional()
    .describe('The key for the Skippy Search API.'),
  NEXT_PUBLIC_REDIRECT_API_URL: z
    .string()
    .url()
    .optional()
    .describe('The url of the API for managing redirects.'),
  NEXT_PUBLIC_REDIRECT_API_KEY: z
    .string()
    .optional()
    .describe('The API Key of the API for managing redirects.'),
  NEXT_PUBLIC_CONNECT_PROXY_URL: z
    .string()
    .url()
    .optional()
    .describe('The url of the API for the Connect Proxy.'),
  NEXT_PUBLIC_CONNECT_PROXY_KEY: z
    .string()
    .optional()
    .describe('The Client ID for the Connect Proxy API.'),
  NEXT_PUBLIC_GTM_CONTAINER: z
    .string()
    .optional()
    .describe('The container ID for Google Tag Manager.'),
  NEXT_PUBLIC_GTM_AUTH: z
    .string()
    .optional()
    .describe('The "gtm_auth" param for Google Tag Manager.'),
  NEXT_PUBLIC_GTM_PREVIEW: z
    .string()
    .optional()
    .describe('The "gtm_preview" param for Google Tag Manager.'),
  NEXT_PUBLIC_BASE_URL_MYACCOUNT: z
    .string()
    .optional()
    .describe('The URL for my account.'),
  NEXT_PUBLIC_BASE_URL_FORGOT_PASSWORD: z
    .string()
    .optional()
    .describe('The URL resetting a forgotten password.'),
  NEXT_PUBLIC_BASE_URL_CREATE_ACCOUNT: z
    .string()
    .optional()
    .describe('The URL to create an account.'),
  NEXT_PUBLIC_STAGE: z
    .string()
    .optional()
    .describe('The environment. prod, stage, devint, local.'),
});
export const getPublicConfig = () => {
  // Note: We can't just pass process.env directly to the parser because it's empty. Next.js does magic on direct access.
  return publicConfigSchema.parse({
    NEXT_PUBLIC_SITE_ID: process.env.NEXT_PUBLIC_SITE_ID,
    NEXT_PUBLIC_BASE_URL_AUTH: process.env.NEXT_PUBLIC_BASE_URL_AUTH,
    NEXT_PUBLIC_BASE_URL_CART: process.env.NEXT_PUBLIC_BASE_URL_CART,
    NEXT_PUBLIC_BASE_URL_CHECKOUT: process.env.NEXT_PUBLIC_BASE_URL_CHECKOUT,
    NEXT_PUBLIC_BASE_URL_DOTCOM: process.env.NEXT_PUBLIC_BASE_URL_DOTCOM,
    NEXT_PUBLIC_BASE_URL_PROXY_API: process.env.NEXT_PUBLIC_BASE_URL_PROXY_API,
    NEXT_PUBLIC_BASE_URL_STORYBOOK: process.env.NEXT_PUBLIC_BASE_URL_STORYBOOK,
    NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
    NEXT_PUBLIC_SKIPPY_SEARCH_API_URL:
      process.env.NEXT_PUBLIC_SKIPPY_SEARCH_API_URL,
    NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY:
      process.env.NEXT_PUBLIC_SKIPPY_SEARCH_API_KEY,
    NEXT_PUBLIC_REDIRECT_API_URL: process.env.NEXT_PUBLIC_REDIRECT_API_URL,
    NEXT_PUBLIC_REDIRECT_API_KEY: process.env.NEXT_PUBLIC_REDIRECT_API_KEY,
    NEXT_PUBLIC_CONNECT_PROXY_URL: process.env.NEXT_PUBLIC_CONNECT_PROXY_URL,
    NEXT_PUBLIC_CONNECT_PROXY_KEY: process.env.NEXT_PUBLIC_CONNECT_PROXY_KEY,
    NEXT_PUBLIC_GTM_CONTAINER: process.env.NEXT_PUBLIC_GTM_CONTAINER,
    NEXT_PUBLIC_GTM_AUTH: process.env.NEXT_PUBLIC_GTM_AUTH,
    NEXT_PUBLIC_GTM_PREVIEW: process.env.NEXT_PUBLIC_GTM_PREVIEW,
    NEXT_PUBLIC_BASE_URL_MYACCOUNT: process.env.NEXT_PUBLIC_BASE_URL_MYACCOUNT,
    NEXT_PUBLIC_BASE_URL_FORGOT_PASSWORD:
      process.env.NEXT_PUBLIC_BASE_URL_FORGOT_PASSWORD,
    NEXT_PUBLIC_BASE_URL_CREATE_ACCOUNT:
      process.env.NEXT_PUBLIC_BASE_URL_CREATE_ACCOUNT,
    NEXT_PUBLIC_STAGE: process.env.NEXT_PUBLIC_STAGE,
  });
};

/**
 * Feature Flags should be short lived. Once the feature is fully rolled out, the flag, and matching logic, should be removed.
 * Can be set per-environment by updating the matching secret in AWS Secrets Manager.
 * Can be overridden locally by using a .env.local file.
 * All feature flags should be booleans with a default of false. Since env vars are strings, we transform them to booleans.
 */
const featureFlagSchema = z.object({
  enableFormBuilder: stringToBool,
  enableRedirects: stringToBool,
  enableSkippySearch: stringToBool,
  disableFieldsOnPublish: stringToBool,
  disableThirdPartyScripts: stringToBool,
});
export const getFeatureFlags = () => {
  return featureFlagSchema.parse({
    enableFormBuilder: process.env.NEXT_PUBLIC_FF_enableFormBuilder,
    enableRedirects: process.env.NEXT_PUBLIC_FF_enableRedirects,
    enableSkippySearch: process.env.NEXT_PUBLIC_FF_enableSkippySearch,
    disableFieldsOnPublish: process.env.NEXT_PUBLIC_FF_disableFieldsOnPublish,
    disableThirdPartyScripts:
      process.env.NEXT_PUBLIC_FF_disableThirdPartyScripts,
  });
};

/**
 * Ensure the environment variables match what we expect. Missing variables will throw.
 * We return all env variables back as strings, so they can be passed on to the SST deploy.
 */
export const validateEnvironmentVariables = () => {
  return envVarsSchema.parse({
    ...getSecrets(),
    ...getPublicConfig(),
    ...getFeatureFlags(),
  });
};

/**
 * Static configuration that does not need to be controlled per-environment.
 */
export const generalConfig = {
  // If we have more pages than this, they will render on-demand instead of at build time.
  maxStaticPagesToBuild: 50000,
  // Sitemaps cap at 50k as per https://developers.google.com/search/docs/crawling-indexing/sitemaps/build-sitemap#general-guidelines.
  singleSitemapLimit: 50000,
  maxContentfulRetries: 5,
  defaultFundId: 'MN',
  defaultFundTridionId: 'tcm:5-102864',
  catalogFilterMin: 5,
  catalogFilterMax: 2000,
  catalogPerPage: 9,
  sacMinAge: 1,
  sacMaxAge: 22,
  cart: {
    transactionMax: 600000,
  },
  donation: {
    amountMin: 5,
    amountMax: 600000,
    optionsMin: 2,
    optionsMax: 5,
  },
  rate: 43,
  publisher: {
    name: 'Compassion International',
    url: 'https://www.compassion.com/',
    logoUrl: 'https://www.compassion.com/compassion-logo.png',
  },
  domain: {
    compassion: 'https://www.compassion.com',
  },
  socials: {
    facebook: 'https://www.facebook.com/compassionintl',
    twitter: 'https://x.com/compassion',
    instagram: 'https://www.instagram.com/compassion/#',
    youtube: 'https://www.youtube.com/user/CompassionIntl',
    linkedIn: 'https://linkedin.com/company/compassion-international',
    pinterest: 'https://www.pinterest.com/compassion/',
    wikipedia: 'https://en.wikipedia.org/wiki/Compassion_International',
  },
  contact: {
    telephone: '(800) 336-7676',
    areaServed: 'United States',
    countryCode: 'US',
  },
  address: {
    streetAddress: '12290 Voyager Pkwy',
    addressLocality: 'Colorado Springs',
    addressRegion: 'CO',
    postalCode: '80921',
    addressCountry: 'US',
  },
  images: {
    homepage: 'https://www.compassion.com/compassion-logo.png',
  },
  sitemap: {
    minArticlesForTag: 3,
  },
} as const;
